import Drawer from '@/components/Drawer';
import { useSubmitOnEnter } from '@/components/Form/useSubmitOnEnter';
import { EMAIL_CENTER_ACTIONS } from '@/constants/actions';
import * as EmailCenterSelectors from '@/selectors/emailCenterSelectors';
import {
  createPartnerTranslationRequest,
  updatePartnerTranslationRequest,
} from '@/services/api/emailCenter';
import { PartnerTranslationModel } from '@/typings/api/emailCenter';
import { applyServerErrorsToForm, normalizeApiError } from '@/utils/apiErrors';
import { F, getIntlTranslation, INTL_ACTION, INTL_ENTITY } from '@/utils/intl';
import { processAPIResponseNow } from '@/utils/request';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import {
  Alert,
  AutoComplete,
  Button,
  Form,
  Input,
  Select,
  Space,
  Typography,
} from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useIntl, useSelector } from 'umi';
import { bindingExpression } from '../../../components/EmailBodyEditor/translationBinding';
import VariableTextField from '../../../components/VariableTextField';
import { objectToRows, rowsToObject } from '../../../keyValue';
import { translationGroups } from '../../../translationCatalog';

import { useEmailCenterValidateMessages } from '../../../useValidateMessages';
import styles from '../settings.less';

/**
 * How to actually use the thing being created.
 *
 * A partner translation is inert on its own: it does nothing until a template looks it
 * up, and the only thing that looks it up is `{{ 'group.key'|trans }}` — a syntax
 * nothing on this screen previously spelled out. So the drawer says it, built from the
 * group and key as they are typed, which also makes the group/key split concrete: you
 * can see the two fields becoming one identifier.
 *
 * Copyable only once both halves are filled. Before that the line is a *pattern* with
 * `group.key` standing in, and a copy button on a pattern hands out something that
 * resolves nowhere.
 */
const ReferenceHint: React.FC<{ group?: string; keyName?: string }> = ({
  group,
  keyName,
}) => {
  const complete = !!group?.trim() && !!keyName?.trim();
  const expression = bindingExpression(
    `${group?.trim() || 'group'}.${keyName?.trim() || 'key'}`,
  );

  return (
    <div className={styles.referenceHint}>
      <span>
        <F
          id="pages.emailCenter.settings.translations.referenceHint"
          defaultMessage="Use it in a template as"
        />{' '}
        <Typography.Text
          code
          copyable={complete ? { text: expression } : false}
        >
          {expression}
        </Typography.Text>
      </span>
      <span className={styles.referenceHintTip}>
        <F
          id="pages.emailCenter.settings.translations.referenceHintTip"
          defaultMessage="Or type {keyword}."
          values={{ keyword: <code>{'{{ trans'}</code> }}
        />
      </span>
    </div>
  );
};

const TranslationDrawer: React.FC = () => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const validateMessages = useEmailCenterValidateMessages();
  const [form] = Form.useForm();

  // The partner's own list, not the product's full one — see
  // `selectTranslationLanguages`. Unset means every language.
  const languageOptions = useSelector(
    EmailCenterSelectors.selectTranslationLanguageOptions,
  );

  const drawer = useSelector(EmailCenterSelectors.selectDrawer('translation'));
  const translations = useSelector(EmailCenterSelectors.selectTranslations);
  const variableGroups = useSelector(
    EmailCenterSelectors.selectTranslationVariableGroups,
  );
  const [saving, setSaving] = useState(false);
  const [generalError, setGeneralError] = useState<string | null>(null);

  /**
   * The groups the partner already uses.
   *
   * An AutoComplete, not a Select: the backend stores the group as a free string,
   * so typing a name that isn't listed is how a new group is created. The list is
   * only a shortcut for reusing a name already in play — which is why it is
   * derived from the loaded catalog rather than fetched. `GET /translation-groups`
   * also offers the *global* catalog's groups, but a group name without its keys
   * is not enough to write a working override, so offering those here only
   * invited translations filed under an official-looking group that resolve
   * nowhere.
   */
  const groupOptions = useMemo(
    () => translationGroups(translations).map((group) => ({ value: group })),
    [translations],
  );

  const record = drawer.record as PartnerTranslationModel | null;
  const isEdit = drawer.mode === 'edit' && record != null;

  useEffect(() => {
    if (!drawer.visible) return;
    setGeneralError(null);
    if (isEdit && record) {
      form.setFieldsValue({
        translation_group: record.group,
        key: record.key,
        values: objectToRows(record.value),
      });
    } else {
      form.resetFields();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawer.visible, record?.id]);

  const close = useCallback(() => {
    dispatch({
      type: EMAIL_CENTER_ACTIONS.CLOSE_DRAWER,
      payload: { drawer: 'translation' },
    });
  }, [dispatch]);

  const handleSave = useCallback(async () => {
    const values = await form.validateFields();
    const payload = {
      translation_group: values.translation_group,
      key: values.key,
      value: rowsToObject(values.values),
    };

    setSaving(true);
    setGeneralError(null);
    const result = isEdit
      ? await updatePartnerTranslationRequest({
          params: { uuid: record!.id },
          payload,
        })
      : await createPartnerTranslationRequest({ payload });
    setSaving(false);

    processAPIResponseNow(result, {
      showSuccessMessage: true,
      successEntity: INTL_ENTITY.PARTNER_TRANSLATION,
      onSuccess: () => {
        close();
        dispatch({ type: EMAIL_CENTER_ACTIONS.FETCH_TRANSLATIONS });
      },
      onError: (data) => {
        const normalized = normalizeApiError(data);
        applyServerErrorsToForm(form, normalized.fieldErrors, {
          // Server: value.de → the row list; a per-row mapping is not
          // reconstructible, so surface those on the list via the alert.
          onUnmapped: (key, messages) =>
            setGeneralError((prev) => prev ?? `${key}: ${messages.join(' ')}`),
        });
        if (Object.keys(normalized.fieldErrors).length === 0) {
          setGeneralError(normalized.message);
        }
      },
    });
  }, [form, isEdit, record, close, dispatch]);

  const submitOnEnter = useSubmitOnEnter(handleSave, { enabled: !saving });

  return (
    <Drawer
      width={520}
      open={drawer.visible}
      onOpenChange={close}
      title={
        <F
          entity={INTL_ENTITY.PARTNER_TRANSLATION}
          action={isEdit ? INTL_ACTION.EDIT : INTL_ACTION.CREATE}
        />
      }
      footer={
        <Space className={styles.drawerFooter}>
          <Button onClick={close}>
            <F id="common.actions.cancel" defaultMessage="Cancel" />
          </Button>
          <Button type="primary" loading={saving} onClick={handleSave}>
            <F id="common.actions.save" defaultMessage="Save" />
          </Button>
        </Space>
      }
    >
      {generalError && (
        <Alert
          className={styles.generalError}
          type="error"
          showIcon
          message={generalError}
        />
      )}

      <Form
        {...submitOnEnter}
        validateMessages={validateMessages}
        form={form}
        layout="vertical"
        initialValues={{ values: [{ key: 'de' }] }}
      >
        <Form.Item
          name="translation_group"
          label={
            <F
              id="pages.emailCenter.settings.translations.group"
              defaultMessage="Group"
            />
          }
          rules={[{ required: true }, { max: 255 }]}
          tooltip={
            <F
              id="pages.emailCenter.settings.translations.groupHint"
              defaultMessage="Groups related keys together, e.g. 'emails'. Pick an existing group or type a new name to create one."
            />
          }
        >
          <AutoComplete
            options={groupOptions}
            filterOption={(input, option) =>
              String(option?.value ?? '')
                .toLowerCase()
                .includes(input.toLowerCase())
            }
          />
        </Form.Item>

        <Form.Item
          name="key"
          label={
            <F
              id="pages.emailCenter.settings.translations.key"
              defaultMessage="Key"
            />
          }
          rules={[{ required: true }, { max: 255 }]}
        >
          <Input />
        </Form.Item>

        {/* Under the two fields it is built from, above the wording: this is what
            joins them, and it answers "and then what?" before the values are typed. */}
        <Form.Item
          noStyle
          shouldUpdate={(prev, current) =>
            prev.translation_group !== current.translation_group ||
            prev.key !== current.key
          }
        >
          {({ getFieldValue }) => (
            <ReferenceHint
              group={getFieldValue('translation_group')}
              keyName={getFieldValue('key')}
            />
          )}
        </Form.Item>

        <Form.Item
          label={
            <F
              id="pages.emailCenter.settings.translations.values"
              defaultMessage="Values"
            />
          }
          required
        >
          <Form.List
            name="values"
            rules={[
              {
                validator: (_, rows: { key?: string }[] = []) =>
                  rows.some((row) => row?.key)
                    ? Promise.resolve()
                    : Promise.reject(
                        getIntlTranslation(intl, {
                          id: 'pages.emailCenter.settings.translations.valuesRequired',
                          defaultMessage: 'Add at least one language value.',
                        }),
                      ),
              },
            ]}
          >
            {(fields, { add, remove }, { errors }) => (
              <>
                {fields.map((field) => (
                  <div key={field.key} className={styles.langRow}>
                    {/**
                     * A language already used by another row is left out here.
                     *
                     * One key holds one wording per language, so two rows for
                     * the same one is not a second translation — it is two
                     * answers to the same question, and only the last would
                     * survive the save.
                     *
                     * Hidden rather than shown greyed out: with the list
                     * already narrowed to the partner's own languages there are
                     * only a handful of options, and a disabled entry among two
                     * or three is more clutter than information.
                     *
                     * The row's own value is always kept, or its select would
                     * render blank where its current language should be.
                     */}
                    <Form.Item
                      noStyle
                      shouldUpdate={(prev, current) =>
                        prev.values !== current.values
                      }
                    >
                      {({ getFieldValue }) => {
                        const rows: { key?: string }[] =
                          getFieldValue('values') ?? [];
                        const mine = rows[field.name]?.key;
                        const taken = new Set(
                          rows
                            .map((row) => row?.key)
                            .filter(
                              (code, index) => code && index !== field.name,
                            ),
                        );

                        return (
                          <Form.Item
                            name={[field.name, 'key']}
                            rules={[{ required: true }]}
                            className={styles.langCode}
                          >
                            <Select
                              options={languageOptions.filter(
                                (option) =>
                                  option.value === mine ||
                                  !taken.has(option.value),
                              )}
                            />
                          </Form.Item>
                        );
                      }}
                    </Form.Item>
                    <Form.Item
                      name={[field.name, 'value']}
                      rules={[{ required: true }]}
                      className={styles.langValue}
                    >
                      <VariableTextField
                        autoSize={{ minRows: 1, maxRows: 4 }}
                        variableGroups={variableGroups}
                      />
                    </Form.Item>
                    <Button
                      type="text"
                      icon={<MinusCircleOutlined />}
                      onClick={() => remove(field.name)}
                    />
                  </div>
                ))}
                <Button
                  type="dashed"
                  icon={<PlusOutlined />}
                  onClick={() => add()}
                  block
                >
                  <F
                    id="pages.emailCenter.settings.translations.addLanguage"
                    defaultMessage="Add language"
                  />
                </Button>
                <Form.ErrorList errors={errors} />
              </>
            )}
          </Form.List>
        </Form.Item>
      </Form>
    </Drawer>
  );
};

export default TranslationDrawer;
